import React, { useEffect, useRef, useState } from 'react'

import { useDashboardRedux } from '@hooks'

import CategoryList from './CategoryList'
import styles from './CategorySelector.module.css'

const CategorySelector = ({ menuListRef }) => {
    const [isSticky, setIsSticky] = useState(false)
    const [isExpanded, setIsExpanded] = useState(true)
    const { categories, selectedCategory, setSelectedCategory } = useDashboardRedux()
    const categoryRef = useRef(null)
    const placeholderRef = useRef(null)
    const [lastScrollTop, setLastScrollTop] = useState(0)

    useEffect(() => {
        const handleScroll = () => {
            if (!categoryRef.current || !placeholderRef.current || !menuListRef.current) return

            const categoryTop = placeholderRef.current.getBoundingClientRect().top
            setIsSticky(categoryTop <= 56)

            const scrollTop = window.pageYOffset || document.documentElement.scrollTop
            const scrollingDown = scrollTop > lastScrollTop
            setLastScrollTop(scrollTop)

            const baseOffset = isExpanded ? 180 : 90
            const headerHeight = 200

            // according to the scroll direction, select different traversal methods
            if (scrollingDown) {
                // when scrolling down, traverse from the start to the end
                for (let i = 0; i < categories.length; i++) {
                    const category = categories[i]
                    const section = menuListRef.current.querySelector(`[data-category="${category}"]`)
                    if (section) {
                        const { bottom } = section.getBoundingClientRect()
                        // if it is the first category, add the header height
                        const offset = i === 0 ? baseOffset + headerHeight : baseOffset
                        if (bottom >= offset) {
                            if (category !== selectedCategory) {
                                setSelectedCategory(category)
                                const activeCategoryElement = categoryRef.current?.querySelector(
                                    `[data-category-item="${category}"]`
                                )
                                if (activeCategoryElement) {
                                    activeCategoryElement.scrollIntoView({ behavior: 'smooth', inline: 'center' })
                                }
                            }
                            break
                        }
                    }
                }
            } else {
                // when scrolling up, traverse from the end to the start
                for (let i = categories.length - 1; i >= 0; i--) {
                    const category = categories[i]
                    const section = menuListRef.current.querySelector(`[data-category="${category}"]`)
                    if (section) {
                        const { top } = section.getBoundingClientRect()
                        // if it is the first category, add the header height
                        const offset = i === 0 ? baseOffset + headerHeight : baseOffset
                        if (top <= offset) {
                            if (category !== selectedCategory) {
                                setSelectedCategory(category)
                                const activeCategoryElement = categoryRef.current?.querySelector(
                                    `[data-category-item="${category}"]`
                                )
                                if (activeCategoryElement) {
                                    activeCategoryElement.scrollIntoView({ behavior: 'smooth', inline: 'center' })
                                }
                            }
                            break
                        }
                    }
                }
            }
        }

        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [categories, selectedCategory, setSelectedCategory, menuListRef, isExpanded, lastScrollTop])

    // when a category is selected, scroll to the corresponding section in `MenuList`
    const handleCategorySelect = category => {
        setSelectedCategory(category)

        if (!menuListRef.current) return

        // find the corresponding category section
        const section = menuListRef.current.querySelector(`[data-category="${category}"]`)

        if (section) {
            const categoryIndex = categories.indexOf(category)

            // if the selected category is the first category, scroll to the top
            if (categoryIndex === 0) {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                })
                return
            }

            const baseOffset = isExpanded ? 180 : 90
            const currentCategory = selectedCategory
            const currentIndex = categories.indexOf(currentCategory)
            const isFromFirstCategory = currentIndex === 0

            const offset = isFromFirstCategory ? baseOffset - 150 : baseOffset
            const extraOffset =
                isFromFirstCategory ?
                    isExpanded ? -308
                    :   -148
                :   0

            window.scrollTo({
                top: section.offsetTop - offset + extraOffset,
                behavior: 'smooth',
            })
        }
    }

    return (
        <>
            {/* Placeholder to maintain layout when sticky */}
            <div ref={placeholderRef} className={styles.placeholder}></div>

            <div ref={categoryRef} className={`${styles.container} ${isSticky ? styles.sticky : ''}`}>
                {/* Scrollable Category List (Hidden when expanded) */}
                <div className={`${styles.scrollContainer} ${isExpanded ? styles.hidden : ''}`}>
                    <CategoryList
                        categories={categories}
                        isExpanded={false}
                        selectedCategory={selectedCategory}
                        onSelectCategory={handleCategorySelect}
                    />
                </div>

                {/* Expanded Full View */}
                <div className={`${styles.expandedContainer} ${isExpanded ? styles.expanded : styles.hidden}`}>
                    <CategoryList
                        categories={categories}
                        isExpanded={true}
                        selectedCategory={selectedCategory}
                        onSelectCategory={handleCategorySelect}
                    />
                </div>

                {/* Expand Button */}
                <button className={styles.expandButton} onClick={() => setIsExpanded(!isExpanded)}>
                    <i className={`bx ${isExpanded ? 'bx-chevron-up' : 'bx-chevron-down'}`} />
                </button>
            </div>
        </>
    )
}

export default CategorySelector
